import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../contexts/AuthContext';
import { getUserProjects } from '../utils/api';

const SettingsPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [saveMessage, setSaveMessage] = useState({ show: false, type: '', message: '' });

  useEffect(() => {
    if (currentUser) {
      loadProjects();
      // Load selected project from localStorage
      let savedProjectId = localStorage.getItem('selectedProjectId');

      // Fallback: if no ID saved, try to get it from the full project object
      if (!savedProjectId) {
        const savedProjectObj = localStorage.getItem('selectedProject');
        if (savedProjectObj) {
          try {
            const project = JSON.parse(savedProjectObj);
            savedProjectId = project.id.toString();
            // Save the ID for consistency
            localStorage.setItem('selectedProjectId', savedProjectId);
          } catch (error) {
            console.error('Error parsing saved project:', error);
          }
        }
      }

      if (savedProjectId) {
        setSelectedProject(savedProjectId);
      }
    }
  }, [currentUser]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await getUserProjects(currentUser.id);
      if (response.success) {
        setProjects(response.projects);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      setSaveMessage({ show: true, type: 'error', message: 'Failed to load projects' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = () => {
    setSaving(true);

    try {
      // Save selected project to localStorage
      if (selectedProject) {
        localStorage.setItem('selectedProjectId', selectedProject);

        // Also save the full project object for easy access
        const project = projects.find(p => p.id === parseInt(selectedProject));
        if (project) {
          localStorage.setItem('selectedProject', JSON.stringify(project));
          console.log('[SettingsPage] Saved project to localStorage:', project);
        }

        // Dispatch custom event to notify NavigationBar and other components
        window.dispatchEvent(new Event('projectChanged'));
        console.log('[SettingsPage] Dispatched projectChanged event');

        setSaveMessage({
          show: true,
          type: 'success',
          message: 'Settings saved successfully! Your project selection has been updated.'
        });

        // Redirect to home page after 2 seconds
        setTimeout(() => {
          navigate('/home');
        }, 2000);
      } else {
        setSaveMessage({
          show: true,
          type: 'warning',
          message: 'Please select a project'
        });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage({
        show: true,
        type: 'error',
        message: 'Failed to save settings'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={3}>
          <SettingsIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Settings
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* User Info */}
        <Card variant="outlined" sx={{ mb: 4, backgroundColor: 'background.default' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              User Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Email: <strong>{currentUser?.email}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Role: <strong>{currentUser?.role}</strong>
            </Typography>
          </CardContent>
        </Card>

        {/* Project Selection */}
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            <BusinessIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Project Selection
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Select your active project. This project will be used throughout the application until you change it.
          </Typography>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="project-select-label">Active Project</InputLabel>
            <Select
              labelId="project-select-label"
              id="project-select"
              value={selectedProject}
              label="Active Project"
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              <MenuItem value="">
                <em>Select a project</em>
              </MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedProject && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Current project: <strong>{projects.find(p => p.id === parseInt(selectedProject))?.name}</strong>
            </Alert>
          )}
        </Box>

        {/* Save Message */}
        {saveMessage.show && (
          <Alert
            severity={saveMessage.type}
            sx={{ mb: 3 }}
            onClose={() => setSaveMessage({ ...saveMessage, show: false })}
          >
            {saveMessage.message}
          </Alert>
        )}

        {/* Action Buttons */}
        <Box display="flex" gap={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={() => navigate('/home')}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSaveSettings}
            disabled={saving || !selectedProject}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default SettingsPage;
